/* global globalThis */
import {createStore} from "redux";
import {rootReducer} from "../../../redux/reducers";
import {
  selectCitiesCache,
  selectCurrentPlayer,
  selectGameResults,
  selectPhase,
  selectPlayers,
  selectRound,
  selectUnits,
  selectWarPhaseStage
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {PHASES} from "../../../constants/phases";
import {handleCompleteTurn} from "../../gameState/turn/handleCompleteTurn";
import {executeBotStep} from "../executeBotStep";
import {restoreBotStrategies, snapshotBotStrategies} from "../strategy/botStrategyStore";
import {resetMeleeHandlingTracking} from "../decisions/decideCombatStep";
import {getBotPredictions, setBotPredictions} from "./botPredictionsStore";
import {getEnemyQuarterHexKeys, getOwnQuarterHexKeys} from "../evaluation/cities";
import {getBotGridRenderer, getNeighborHexKeys} from "../botGrid";

// Кап шагов песочницы на игрока (страховка от зацикливания симуляции)
const SANDBOX_STEPS_PER_PLAYER = 250;
// Столько подряд шагов без смены (игрок|этап) — принудительное завершение хода
const SANDBOX_STALL_LIMIT = 8;

/**
 * «Сильный режим»: перед своей фазой войны бот симулирует в песочнице фазы войны
 * всех СЛЕДУЮЩИХ игроков текущего раунда по основному алгоритму (свои этапы в
 * песочнице пропускаются без действий — прогнозируется мир, где мы еще не ходили)
 * и сохраняет прогноз: позиции чужих отрядов, свои кварталы под прогнозируемым
 * захватом и чужие кварталы, которые защита покинет. Прогноз кэшируется на раунд.
 *
 * Песочница изолирована полностью: отдельный store на глубокой копии состояния,
 * стратегическое состояние ботов снимается и восстанавливается, звуки и анимации
 * отключаются флагом __isBotSandbox. Вложенных прогнозов нет — боты в песочнице
 * работают без сильного режима.
 */
export const ensureOpponentPredictions = (store, currentPlayer, options = {}) => {
  const state = store.getState();
  const round = selectRound(state);
  if (getBotPredictions(currentPlayer.id, round)) {
    return;
  }

  const players = selectPlayers(state);
  const strategySnapshot = snapshotBotStrategies();
  // Дополнительные изолируемые хранилища (симуляции: стратегии legacy-движка)
  const auxStores = globalThis.__strongModeAuxStrategyStores || [];
  const auxSnapshots = auxStores.map((auxStore) => auxStore.snapshot());
  let sandboxFinalState = null;

  // Диагностический режим (симуляции): песочница не запускается вовсе —
  // проверяется только канал снимка/восстановления стратегий
  // (__strongModeNoRestore: и восстановление тоже пропускается)
  if (globalThis.__strongModeSandboxSkip) {
    if (!globalThis.__strongModeNoRestore) {
      restoreBotStrategies(strategySnapshot);
    }
    setBotPredictions(currentPlayer.id, round, {
      predictedUnits: [],
      predictedEnemyCellSizes: new Map(),
      predictedPositionByUnitId: new Map(),
      threatenedOwnQuarters: new Set(),
      vacatedEnemyQuarters: new Set(),
    });
    return;
  }

  try {
    const sandbox = createStore(rootReducer, JSON.parse(JSON.stringify(state)));
    sandbox.__isBotSandbox = true;

    // Бои песочницы не должны наследовать трекинг реальных боев (и наоборот)
    resetMeleeHandlingTracking();

    const maxSteps = SANDBOX_STEPS_PER_PLAYER * Math.max(1, players.length);
    let lastFingerprint = '';
    let stallCounter = 0;

    for (let step = 0; step < maxSteps; step++) {
      const sandboxState = sandbox.getState();
      if (selectGameResults(sandboxState)) break;
      if (selectPhase(sandboxState) !== PHASES.WAR) break; // фаза войны раунда закончена
      if (selectRound(sandboxState) !== round) break;
      const sandboxPlayer = selectCurrentPlayer(sandboxState);
      if (!sandboxPlayer) break;

      const fingerprint = `${sandboxPlayer.id}|${selectWarPhaseStage(sandboxState) || ''}`;
      if (fingerprint === lastFingerprint) {
        stallCounter += 1;
      } else {
        stallCounter = 0;
        lastFingerprint = fingerprint;
      }

      if (sandboxPlayer.id === currentPlayer.id || !sandboxPlayer.isBot
        || stallCounter > SANDBOX_STALL_LIMIT
        || globalThis.__strongModeFastForwardOnly
      ) {
        // Свои этапы и этапы живых игроков пропускаются без действий;
        // застрявший этап (например, ожидание ближнего боя) завершается принудительно
        handleCompleteTurn(sandbox, true, false, null);
        continue;
      }

      // Симуляции могут инжектировать движок противника (A/B: legacy-боты
      // прогнозируются их реальным legacy-движком); по умолчанию — основной
      const customStep = globalThis.__strongModeOpponentStepResolver
        && globalThis.__strongModeOpponentStepResolver(sandboxPlayer.id);
      if (customStep) {
        customStep(sandbox);
      } else {
        // Клонированное состояние песочницы наследует gameType исходной игры —
        // без совпадающего options.online внутренняя проверка типа игры не
        // пройдет для онлайн-партий, и шаг молча ничего не сделает (прогноз
        // сведется к принудительным пропускам через застревание, а не к
        // реальным решениям бота)
        executeBotStep(sandbox, {online: options.online});
      }
    }

    sandboxFinalState = sandbox.getState();
  } catch (error) {
    console.error('[Bot] Strong mode sandbox failed', error);
  } finally {
    // Симуляция противников не должна портить их реальное стратегическое состояние
    restoreBotStrategies(strategySnapshot);
    auxStores.forEach((auxStore, index) => auxStore.restore(auxSnapshots[index]));
    // ...и модульный трекинг ближнего боя (ключи песочницы совпадают с реальными)
    resetMeleeHandlingTracking();
    (globalThis.__strongModeSandboxResets || []).forEach((reset) => reset());
  }

  const data = {
    predictedUnits: [],
    predictedEnemyCellSizes: new Map(),
    predictedPositionByUnitId: new Map(),
    threatenedOwnQuarters: new Set(),
    vacatedEnemyQuarters: new Set(),
  };

  if (sandboxFinalState) {
    const realUnits = selectUnits(state);
    const realCitiesCache = selectCitiesCache(state);
    const gridRenderer = getBotGridRenderer(state);

    selectUnits(sandboxFinalState).forEach((unit) => {
      if (unit.ownerId === currentPlayer.id) {
        return;
      }
      data.predictedUnits.push({
        id: unit.id, ownerId: unit.ownerId, hexKey: unit.hexKey, size: unit.size, type: unit.type,
      });
      data.predictedPositionByUnitId.set(unit.id, unit.hexKey);
      data.predictedEnemyCellSizes.set(
        unit.hexKey, (data.predictedEnemyCellSizes.get(unit.hexKey) || 0) + unit.size
      );
    });

    // Свои кварталы, занятые чужими отрядами в прогнозе или потерянные в песочнице
    const ownQuarters = getOwnQuarterHexKeys(realCitiesCache, currentPlayer.color);
    const sandboxOwnQuarters = getOwnQuarterHexKeys(
      selectCitiesCache(sandboxFinalState), currentPlayer.color
    );
    ownQuarters.forEach((cityInfo, quarterHexKey) => {
      if (data.predictedEnemyCellSizes.has(quarterHexKey) || !sandboxOwnQuarters.has(quarterHexKey)) {
        data.threatenedOwnQuarters.add(quarterHexKey);
      }
    });

    // Чужие кварталы, которые защита покидает в прогнозе — окно для захвата
    const enemyQuarters = getEnemyQuarterHexKeys(realCitiesCache, currentPlayer.color);
    const realEnemyUnitCells = new Set();
    realUnits.forEach((unit) => {
      if (unit.ownerId !== currentPlayer.id) {
        realEnemyUnitCells.add(unit.hexKey);
      }
    });
    enemyQuarters.forEach((cityInfo, quarterHexKey) => {
      const nearbyCells = [quarterHexKey, ...getNeighborHexKeys(quarterHexKey, gridRenderer)];
      const hasRealDefender = nearbyCells.some((cellKey) => realEnemyUnitCells.has(cellKey));
      const hasPredictedDefender = nearbyCells.some((cellKey) => data.predictedEnemyCellSizes.has(cellKey));
      if (hasRealDefender && !hasPredictedDefender) {
        data.vacatedEnemyQuarters.add(quarterHexKey);
      }
    });
  }

  // Диагностический «слепой» режим (симуляции): песочница отрабатывает,
  // но прогноз не используется — для локализации канала влияния на A/B
  if (globalThis.__strongModeBlind) {
    data.predictedUnits = [];
    data.predictedEnemyCellSizes = new Map();
    data.predictedPositionByUnitId = new Map();
    data.threatenedOwnQuarters = new Set();
    data.vacatedEnemyQuarters = new Set();
  }

  setBotPredictions(currentPlayer.id, round, data);

  // Инструментация симуляций: счетчик песочных прогнозов (см. simulateBotBatch)
  if (globalThis.__strongModeProbe) {
    globalThis.__strongModeProbe(currentPlayer.id, data);
  }
};
